/**
 * The "Logins on this number" panel of one QR number: its main login plus the extra WhatsApp-Web sessions (linked devices) of the SAME phone
 * number. Shown only when the account has multi-session switched on (maxSessions > 1) -- otherwise the numbers dialog is exactly as before.
 * Which login "receives messages" decides which one processes the number's incoming messages; messages are sent through any connected login.
 */
import React, { useEffect, useState } from "react";
import { Box, Button, Chip, CircularProgress, IconButton, Stack, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import toast from "react-hot-toast";
import { useWhatsAppQrSessions } from "../../../features/whatsapp/hooks/useWhatsAppQrSessions";

const STATE_LABEL = {
  CONNECTED: { text: "Connected", color: "success" },
  QR_PENDING: { text: "Waiting for scan", color: "warning" },
  CREATING: { text: "Starting…", color: "warning" },
  AUTHENTICATING: { text: "Connecting…", color: "warning" },
  RECONNECTING: { text: "Reconnecting…", color: "warning" },
  DISCONNECTED: { text: "Disconnected", color: "default" },
  LOGGED_OUT: { text: "Logged out", color: "default" },
  STOPPED: { text: "Stopped", color: "default" },
  ERROR: { text: "Error", color: "error" },
};
const NEEDS_SCAN = new Set(["LOGGED_OUT", "STOPPED", "ERROR", "DISCONNECTED", null, undefined]);
const LIVE = new Set(["CONNECTED", "RECONNECTING", "AUTHENTICATING"]);
const REMOVABLE = new Set(["LOGGED_OUT", "CREATING", "QR_PENDING", "ERROR", "STOPPED", "DISCONNECTED"]);

// A faint wash in the state's colour, like the number cards above the panel.
const STATE_TINT = { success: "#25D366", warning: "#ED6C02", error: "#D32F2F", default: "#9E9E9E" };
const BOX_MIN_HEIGHT = 170;
const BOX_SX = { minWidth: 0, minHeight: BOX_MIN_HEIGHT, borderRadius: "12px", border: "1px solid", borderColor: "divider" };
const GRID_SX = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: 2, mt: 1.5 };

// One login (the main one or an extra session) as a box of the grid, like the number cards above. `hint` is the note under the badges (main
// login), `actions` the buttons pinned to the bottom (extra sessions).
// showQr: an extra session shows its own QR here; the MAIN login's QR is the one on the number's card above the panel, so its box passes false.
// A box waiting for its scan takes the whole row so the QR has room.
// The "Receive messages here" button that used to sit next to the badge was removed from the UI (owner decision, 2026-10-24); the badge still shows
// which login receives. The hook's setReceiver and the server's PUT .../receiver are unchanged.
const LoginCard = ({ title, state, isReceiver, hint, actions, error, qrDataUrl, showQr = true }) => {
  const meta = STATE_LABEL[state] || { text: state || "Unknown", color: "default" };
  const tint = STATE_TINT[meta.color] || STATE_TINT.default;
  const showingQr = state === "QR_PENDING" && showQr;
  return (
    <Box
      sx={{
        ...BOX_SX,
        gridColumn: showingQr ? "1 / -1" : "auto",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: 2,
        backgroundImage: (theme) => `linear-gradient(to bottom left, ${tint}${theme.palette.mode === "dark" ? "2E" : "22"} 0%, transparent 55%)`,
      }}
    >
      <Typography sx={{ fontWeight: 700, wordBreak: "break-word" }}>{title}</Typography>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={meta.text} color={meta.color} variant="outlined" />
        {isReceiver && <Chip size="small" label="Receives messages" color="primary" />}
      </Stack>
      {hint}
      {showingQr && (
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="Scan to link this session" style={{ width: 220, height: 220, borderRadius: 8, background: "#fff", padding: 8 }} />
          ) : (
            <CircularProgress size={22} />
          )}
          <Typography variant="body2" color="text.secondary" align="center">
            On this number&apos;s phone: WhatsApp → Settings → Linked Devices → Link a Device.
          </Typography>
        </Box>
      )}
      {error && (state === "ERROR" || state === "LOGGED_OUT") && (
        <Typography variant="body2" color="error">{error}</Typography>
      )}
      {actions && <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: "auto", pt: 1 }}>{actions}</Stack>}
    </Box>
  );
};

const WhatsAppQrSessionsPanel = ({ workspaceId, number, maxSessions }) => {
  const { data, loading, error, addSession, connectSession, logoutSession, removeSession } = useWhatsAppQrSessions(workspaceId, number.connectionId, true);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true); // the owner can fold the panel away to see just the number
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");

  const guard = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (_) {
      // the hook keeps the server's message in `error`, shown below
    } finally {
      setBusy(false);
    }
  };

  // Errors of these actions show as the app's normal top-right toast, not inside the panel.
  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const sessions = data?.sessions || [];
  const mainState = data?.mainSessionState || number.mainSessionState || number.state;
  // `receiver` is the login that really processes incoming messages right now (the server applies the same rule as the worker). It is the login the
  // owner chose, or -- while that one is logged out / in error -- another connected login covering for it; the badge follows it. The yellow
  // "chosen login is ... another login is receiving" note that used to be shown for that case was removed (owner decision, 2026-10-24).
  const receiver = data?.receiver || "main";
  const submitAdd = () =>
    guard(async () => {
      await addSession(label.trim());
      setAdding(false);
      setLabel("");
      setOpen(true); // its QR is shown inside the panel
    });
  const connectedCount = (LIVE.has(mainState) ? 1 : 0) + sessions.filter((s) => s.state === "CONNECTED").length;

  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ fontWeight: 600 }}>
          Logins on this number ({1 + sessions.length}/{maxSessions})
          {!open && <Typography component="span" variant="body2" color="text.secondary"> — {connectedCount} connected</Typography>}
        </Typography>
        <IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label={open ? "Hide logins" : "Show logins"} aria-expanded={open}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Stack>
      {open && (
        <>
      {/* The "Each login is a WhatsApp-Web session ..." and "WhatsApp allows about 4 linked devices ..." explanations that used to be shown here were
          removed (owner decision, 2026-10-24) so the numbers and their logins read cleanly. Nothing behind them changed. */}
      {loading && !data ? (
        <CircularProgress size={20} />
      ) : (
        // The main login and every extra session are boxes in a grid (like the number cards), not a column.
        <Box sx={GRID_SX}>
          <LoginCard
            title="Session 1 (main login)"
            state={mainState}
            isReceiver={receiver === "main"}
            showQr={false}
            hint={
              <>
                {mainState === "QR_PENDING" && (
                  <Typography variant="body2" color="text.secondary">Scan the QR code shown above with this number&apos;s phone to link the main login again.</Typography>
                )}
                {NEEDS_SCAN.has(mainState) && (
                  <Typography variant="body2" color="text.secondary">Use "Connect (scan QR)" above to link the main login again.</Typography>
                )}
              </>
            }
          />
          {sessions.map((s) => (
            <LoginCard
              key={s.sessionId}
              title={s.title || s.label}
              state={s.state}
              isReceiver={receiver === s.sessionId}
              error={s.lastErrorMessage}
              qrDataUrl={s.qrDataUrl}
              actions={
                <>
                  {NEEDS_SCAN.has(s.state) && (
                    <Button size="small" variant="outlined" disabled={busy} onClick={() => guard(() => connectSession(s.sessionId))} sx={{ textTransform: "none" }}>
                      {s.state === "LOGGED_OUT" ? "Connect (scan QR)" : "Reconnect"}
                    </Button>
                  )}
                  {s.state !== "LOGGED_OUT" && s.state !== "CREATING" && (
                    <Button size="small" color="error" disabled={busy} onClick={() => guard(() => logoutSession(s.sessionId))} sx={{ textTransform: "none" }}>
                      Disconnect
                    </Button>
                  )}
                  {REMOVABLE.has(s.state) && (
                    <Button size="small" color="inherit" disabled={busy} onClick={() => guard(() => removeSession(s.sessionId))} sx={{ textTransform: "none" }}>
                      Remove
                    </Button>
                  )}
                </>
              }
            />
          ))}
          {data?.canAddSession &&
            (adding ? (
              // The name form takes the whole row.
              <Box sx={{ ...BOX_SX, minHeight: 0, gridColumn: "1 / -1", p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <TextField size="small" label="Name (optional, e.g. Reception)" value={label} onChange={(e) => setLabel(e.target.value)} inputProps={{ maxLength: 60 }} />
                  <Button size="small" variant="contained" disabled={busy} onClick={submitAdd} sx={{ textTransform: "none" }}>Add session</Button>
                  <Button size="small" color="inherit" disabled={busy} onClick={() => { setAdding(false); setLabel(""); }} sx={{ textTransform: "none" }}>Cancel</Button>
                </Stack>
              </Box>
            ) : (
              <Box
                role="button"
                tabIndex={0}
                aria-disabled={busy}
                onClick={() => { if (!busy) setAdding(true); }}
                onKeyDown={(e) => {
                  if (!busy && e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    setAdding(true);
                  }
                }}
                sx={{
                  ...BOX_SX,
                  borderStyle: "dashed",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  cursor: busy ? "default" : "pointer",
                  opacity: busy ? 0.6 : 1,
                  color: "text.secondary",
                  transition: "border-color 0.2s, color 0.2s, background-color 0.2s",
                  "&:hover": busy ? {} : { borderColor: "primary.main", color: "primary.main", backgroundColor: "action.hover" },
                  "&:focus-visible": { outline: (theme) => `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                }}
              >
                <AddIcon sx={{ fontSize: 34 }} />
                <Typography sx={{ fontWeight: 600, color: "inherit" }}>Add session</Typography>
              </Box>
            ))}
        </Box>
      )}
        </>
      )}
    </Box>
  );
};

export default WhatsAppQrSessionsPanel;
